import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, PenTool, Plus, ChevronUp,
  Search, BarChart3, CheckCircle, Clock, FileText
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useMarkingStore } from '../stores/markingStore';
import MarkingUploadForm from '../components/marking/MarkingUploadForm';
import MarkingProgressTracker from '../components/marking/MarkingProgressTracker';
import MarkingResultsView from '../components/marking/MarkingResultsView';
import MarkingJobCard from '../components/marking/MarkingJobCard';
import ManualMarkingWorkspace from '../components/marking/ManualMarkingWorkspace';
import AiMarkingWorkspace from '../components/marking/AiMarkingWorkspace';
import TaskManager from '../components/marking/TaskManager';
import ProfileModal from '../components/auth/ProfileModal';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
];

const MarkingPage = ({ jobId }) => {
  const navigate = useNavigate();
  const { user, userData } = useAuthStore();
  const {
    markingJobs,
    currentJob,
    loadMarkingJobs,
    loadMarkingTasks,
    loadMarkingQuestions,
    loadMarkingJob,
    loadModerationJobs,
    isLoading
  } = useMarkingStore();

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('upload');
  const [showTaskManager, setShowTaskManager] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [moderationJobs, setModerationJobs] = useState([]);
  const [moderationError, setModerationError] = useState('');
  const [jobError, setJobError] = useState('');
  const needsProfileCompletion = userData?.requiresProfileCompletion === true;

  useEffect(() => {
    if (user?.uid && !jobId) {
      loadMarkingJobs(user.uid);
      loadMarkingTasks(user.uid);
      loadMarkingQuestions(user.uid);
    }
  }, [user, jobId, loadMarkingJobs, loadMarkingTasks, loadMarkingQuestions]);

  useEffect(() => {
    if (jobId) {
      setJobError('');
      loadMarkingJob(jobId).catch((error) => setJobError(error.message));
    }
  }, [jobId, loadMarkingJob]);

  const handleJobCreated = (newJobId) => {
    setShowUploadForm(false);
    navigate(`/marking/${newJobId}`);
  };

  const stats = useMemo(() => {
    const total = markingJobs.length;
    const completed = markingJobs.filter((j) => j.status === 'completed').length;
    const active = markingJobs.filter((j) =>
      ['uploading', 'processing', 'analyzing', 'overlaying', 'manual-review', 'ai-review'].includes(j.status)
    ).length;
    const completedWithScores = markingJobs.filter((j) => j.status === 'completed' && j.results?.percentage != null);
    const avgScore = completedWithScores.length > 0
      ? Math.round(completedWithScores.reduce((sum, j) => sum + j.results.percentage, 0) / completedWithScores.length)
      : null;
    return { total, completed, active, avgScore };
  }, [markingJobs]);

  const filteredJobs = useMemo(() => {
    let jobs = markingJobs;
    if (activeFilter === 'active') {
      jobs = jobs.filter((j) => ['uploading', 'processing', 'analyzing', 'overlaying', 'manual-review', 'ai-review'].includes(j.status));
    } else if (activeFilter === 'completed') {
      jobs = jobs.filter((j) => j.status === 'completed');
    } else if (activeFilter === 'failed') {
      jobs = jobs.filter((j) => j.status === 'failed');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      jobs = jobs.filter((j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.subject || '').toLowerCase().includes(q) ||
        (j.learnerName || '').toLowerCase().includes(q)
      );
    }

    return jobs;
  }, [markingJobs, activeFilter, searchQuery]);

  if (jobId && jobError) {
    return <div className="mx-auto max-w-3xl px-6 py-8"><Link to="/marking" className="text-sigma-700 underline">Back to marking</Link><p role="alert" className="mt-4 text-red-700">{jobError}</p></div>;
  }

  if (jobId && currentJob?.id === jobId) {
    if (currentJob.markingType === 'ai-collaborative') {
      return <AiMarkingWorkspace key={jobId} jobId={jobId} onBack={() => navigate('/marking')} />;
    }
    const status = currentJob.status;
    const isManual = currentJob.markingType === 'manual';

    if (status === 'completed') {
      return (
        <div className="max-w-5xl mx-auto px-6 py-8">
          <MarkingResultsView jobId={jobId} onBack={() => navigate('/marking')} />
        </div>
      );
    }

    if (status === 'failed') {
      return (
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/marking')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Marking
          </button>
          <MarkingProgressTracker jobId={jobId} />
          <div className="mt-4">
            <button
              onClick={() => navigate('/marking')}
              className="px-4 py-2 bg-sigma-600 text-white text-sm font-medium rounded-md hover:bg-sigma-700"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    if (isManual) {
      return (
        <div className="h-[calc(100vh-1rem)] w-full max-w-none px-2 py-2">
          <ManualMarkingWorkspace
            jobId={jobId}
            onBack={() => navigate('/marking')}
            onCompleted={() => navigate(`/marking/${jobId}`)}
          />
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/marking')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Marking
        </button>
        <MarkingProgressTracker jobId={jobId} />
      </div>
    );
  }

  if (jobId && (!currentJob || currentJob.id !== jobId)) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-sigma-600 to-sigma-800 p-6 mb-8">
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="p-1.5 text-white/70 hover:text-white rounded-md hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center">
                <PenTool className="w-6 h-6 mr-2" />
                Sigma eMarker
              </h1>
              <p className="text-sigma-100 text-sm mt-0.5">
                AI-powered script marking and assessment
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setActiveMainTab('upload');
                setShowUploadForm((prev) => !prev);
              }}
              className="inline-flex items-center px-4 py-2.5 bg-white text-sigma-700 text-sm font-semibold rounded-lg hover:bg-sigma-50 transition-colors shadow-sm"
            >
              {showUploadForm ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1.5" />
                  Close
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Upload New
                </>
              )}
            </button>
            <button
              onClick={() => {
                setActiveMainTab('tasks');
                setShowUploadForm(false);
              }}
              className="inline-flex items-center px-4 py-2.5 bg-white text-sigma-700 text-sm font-semibold rounded-lg hover:bg-sigma-50 transition-colors shadow-sm"
            >
              Tasks
            </button>
          </div>
        </div>
        <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/5 rounded-full" />
        <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full" />
      </div>

      <details className="mb-6 rounded-xl border border-gray-200 bg-white p-4" onToggle={async (event) => {
        if (!event.currentTarget.open || !user?.uid) return;
        try { setModerationJobs(await loadModerationJobs(user.uid)); setModerationError(''); }
        catch (error) { setModerationError(error.message); }
      }}>
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Independent AI moderation inbox</summary>
        {moderationError && <p role="alert" className="mt-3 text-sm text-red-700">{moderationError}</p>}
        <div className="mt-3 space-y-2">{moderationJobs.length ? moderationJobs.map((job) => <Link key={job.id} to={`/marking/${job.id}`} className="block rounded-lg border border-gray-200 p-3 text-sm hover:bg-sigma-50">
          <span className="font-medium">{job.title}</span><span className="ml-2 text-gray-500">{job.aiWorkflow?.stage === 'moderation' ? 'Awaiting your moderation' : job.aiWorkflow?.stage === 'released' ? 'Results released' : 'Educator review in progress'}</span>
        </Link>) : <p className="text-sm text-gray-500">No AI sessions are currently assigned to you.</p>}</div>
      </details>

      {activeMainTab === 'tasks' && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setShowTaskManager(false)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                !showTaskManager
                  ? 'bg-white text-sigma-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Marking Jobs
            </button>
            <button
              onClick={() => setShowTaskManager(true)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                showTaskManager
                  ? 'bg-white text-sigma-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Task Manager
            </button>
          </div>
        </div>
      )}

      {activeMainTab === 'tasks' && showTaskManager && (
        <div className="mb-8">
          <TaskManager onClose={() => setShowTaskManager(false)} />
        </div>
      )}

      {activeMainTab === 'upload' && showUploadForm && (
        <div className="mb-8">
          <MarkingUploadForm
            onJobCreated={handleJobCreated}
            needsProfileCompletion={needsProfileCompletion}
            onCompleteProfile={() => setShowProfileModal(true)}
          />
        </div>
      )}

      {activeMainTab === 'tasks' && !showTaskManager && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center text-gray-500 text-xs font-medium mb-1">
                <BarChart3 className="w-3.5 h-3.5 mr-1" />
                Total Jobs
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center text-green-600 text-xs font-medium mb-1">
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Completed
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center text-blue-600 text-xs font-medium mb-1">
                <Clock className="w-3.5 h-3.5 mr-1" />
                In Progress
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center text-sigma-600 text-xs font-medium mb-1">
                <BarChart3 className="w-3.5 h-3.5 mr-1" />
                Avg Score
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats.avgScore != null ? `${stats.avgScore}%` : '-'}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeFilter === tab.key
                      ? 'bg-white text-sigma-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sigma-500 focus:border-transparent"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="space-y-3">
              {filteredJobs.map((job) => (
                <MarkingJobCard key={job.id} job={job} />
              ))}
            </div>
          ) : markingJobs.length > 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No jobs match your filter</p>
              <button
                onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}
                className="text-sigma-600 text-sm hover:underline mt-2 inline-block"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
              <div className="w-16 h-16 bg-sigma-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-sigma-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No marking jobs yet</h3>
              <p className="text-gray-500 text-sm mb-4">
                Upload a student script and memo to get started with AI marking.
              </p>
              <button
                onClick={() => setShowUploadForm(true)}
                className="inline-flex items-center px-4 py-2 bg-sigma-600 text-white text-sm font-semibold rounded-lg hover:bg-sigma-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Upload Your First Script
              </button>
            </div>
          )}
        </>
      )}

      {activeMainTab === 'upload' && !showUploadForm && (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <div className="w-16 h-16 bg-sigma-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-sigma-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Create a Marking Job</h3>
          <p className="text-gray-500 text-sm mb-4">
            Use the Upload tab action to start a new script marking workflow.
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center px-4 py-2 bg-sigma-600 text-white text-sm font-semibold rounded-lg hover:bg-sigma-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Upload New
          </button>
        </div>
      )}

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </div>
  );
};

export default MarkingPage;
